import React, { ReactElement } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Typography,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import parse from "html-react-parser";
import "../../styles/common.css";
import CloseIcon from "@mui/icons-material/Close";

type ActionType = "save" | "delete";
interface CustomConfirmProps {
  readonly isOpen: boolean;
  readonly title: string;
  readonly message?: string;
  readonly subTitle?: string;
  readonly width?: string;
  readonly actionBtnLabel?: string;
  readonly cancelBtnLabel?: string;
  readonly hideActionBtn?: boolean;
  readonly disableActionBtn?: boolean;
  readonly isLoading?: boolean;
  readonly handleSuccess: () => void;
  readonly handleClose: () => void;
  readonly additionalInfoSection?: ReactElement | null;
  readonly backgroundcolor?: boolean;
  readonly actionType?: ActionType;
  readonly handleCancel?: () => void
}

const CustomConfirmation: React.FC<CustomConfirmProps> = ({
  isOpen,
  title,
  subTitle,
  message,
  width = "xs",
  actionBtnLabel = "Confirm",
  cancelBtnLabel = "Cancel",
  hideActionBtn = false,
  isLoading = false,
  handleSuccess,
  handleClose,
  additionalInfoSection,
  backgroundcolor,
  actionType,
  handleCancel,
}) => {
  return (
    <Dialog
      open={isOpen}
      fullWidth
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          if (isLoading) {
            return;
          }
          // ✅ behave like Cancel (NOT discard)
          if (handleCancel) {
            handleCancel();
          } else {
            handleClose(); // fallback
          }
          return;
        }
        handleClose();
      }}
      aria-labelledby="confirm-dialog-title"
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 1,
          width: width,
        },
      }}
    >
      {/* Optional Icon and Title */}
      <DialogTitle id="confirm-dialog-title" sx={{ textAlign: "center",paddingBottom: "20px", paddingTop: "24px", position: "relative", }}>
        {handleCancel && 
          <IconButton
            onClick={handleCancel}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "#99A1AF",
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        }
        <Typography
          variant="h1"
          sx={{
            color: "#33333B",
            textAlign: "center",
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      {/* Body Content */}
      {(message || additionalInfoSection) && (
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          {subTitle && (
            <Typography variant="h6"
              sx={{
                color: "#6F6F7F",
                mb: message ? 1 : 0,
              }}
            >
              {subTitle}
            </Typography>
          )}

          {message && (
            <Typography
              sx={{
                color: "#6F6F7F",
                fontSize: 14,
                lineHeight: 1.6,
              }}
            >
              {parse(message)}
            </Typography>
          )}

          {additionalInfoSection && <Box mt={2}>{additionalInfoSection}</Box>}
        </DialogContent>
      )}

      {/* Actions */}
      <DialogActions
        sx={{
          justifyContent: "center",
          pb: 3,
          pt: 0,
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          className="custom-action-cancel-btn"
        >
          {cancelBtnLabel}
        </Button>

        {!hideActionBtn && (
          <Button
            onClick={handleSuccess}
            variant="outlined"
            className={
              backgroundcolor
                ? actionType === "save"
                  ? "custom-action-save-btn"
                  : "custom-action-del-btn"
                : "custom-action-del-btn-noback"
            }
          >
            {isLoading ? `${actionBtnLabel}...` : actionBtnLabel}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CustomConfirmation;